import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ChannelAnalytics } from "@/components/ChannelAnalytics";
import { FollowChannelCard, ManagementPolicyEditor } from "@/components/ManagementPolicyEditor";

export function ChannelDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const channels = useQuery({ queryKey: queryKeys.channels, queryFn: api.channels });
  const channel = channels.data?.channels.find((c) => c.id === id);

  if (channels.isLoading) return <Skeleton className="h-40 w-full" />;
  if (!channel) return <p className="text-sm text-destructive">No such channel.</p>;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">{channel.title || `Channel ${channel.tgChannelId}`}</h1>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {channel.role === "signal" && <TabsTrigger value="rules">Rules</TabsTrigger>}
          {channel.role === "signal" && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
        </TabsList>

        <TabsContent value="settings">
          <ChannelSettingsTab channelId={id} />
        </TabsContent>

        {channel.role === "signal" && (
          <TabsContent value="rules">
            <ChannelRulesTab channelId={id} />
          </TabsContent>
        )}

        {channel.role === "signal" && (
          <TabsContent value="analytics">
            <ChannelAnalytics channelId={id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ChannelSettingsTab({ channelId }: { channelId: number }) {
  const queryClient = useQueryClient();
  const channels = useQuery({ queryKey: queryKeys.channels, queryFn: api.channels });
  const channel = channels.data?.channels.find((c) => c.id === channelId);
  const [title, setTitle] = useState(channel?.title ?? "");

  // There is at most one system channel; grey the option out when some other row holds it.
  const systemTakenByOther = (channels.data?.channels ?? []).some(
    (c) => c.role === "system" && c.archivedAt === null && c.id !== channelId,
  );

  useEffect(() => setTitle(channel?.title ?? ""), [channel?.title]);

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchChannel>[1]) => api.patchChannel(channelId, body),
    onSuccess: (res) => {
      queryClient.setQueryData(queryKeys.channels, (old: { channels: (typeof res.channel)[] } | undefined) => {
        if (!old) return old;
        return { channels: old.channels.map((c) => (c.id === res.channel.id ? res.channel : c)) };
      });
    },
  });

  if (!channel) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Title</label>
          <div className="flex gap-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            <Button
              variant="outline"
              disabled={patch.isPending || title === channel.title}
              onClick={() => patch.mutate({ title })}
            >
              Save
            </Button>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Role</label>
          <select
            value={channel.role}
            disabled={patch.isPending}
            onChange={(e) => patch.mutate({ role: e.target.value as typeof channel.role })}
            className="w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
            aria-label="Channel role"
          >
            <option value="signal">signal — trading signals are read from it</option>
            <option value="system" disabled={systemTakenByOther}>
              {systemTakenByOther
                ? "system — another channel already has this role"
                : "system — receives app status notifications"}
            </option>
            <option value="trades">trades — receives trade notifications</option>
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            system/trades channels are enabled automatically; signal channels start disabled.
          </p>
          {patch.isError && (
            <p className="mt-1 text-xs text-destructive">
              {patch.error instanceof Error ? patch.error.message : "Could not update the channel."}
            </p>
          )}
        </div>

        {channel.role === "signal" ? (
          <ToggleRow
            label="Ingesting"
            description="Whether messages from this channel are read at all."
            checked={channel.enabled}
            disabled={patch.isPending}
            onChange={(checked) => patch.mutate({ enabled: checked })}
          />
        ) : (
          // M18 — for system/trades, `enabled` is the notification-target switch, and
          // calling it "Ingesting" got the app's own channels switched off in production.
          <ToggleRow
            label="Receiving notifications"
            description="This channel is where the app posts its notifications. Off silences them — nothing is ingested from this channel either way."
            checked={channel.enabled}
            disabled={patch.isPending}
            onChange={(checked) => patch.mutate({ enabled: checked })}
          />
        )}

        {channel.role === "signal" && (
          <ToggleRow
            label="Auto-execute"
            description="Whether a clear signal from this channel opens real orders without approval."
            checked={channel.autoExecute}
            disabled={patch.isPending}
            onChange={(checked) => patch.mutate({ autoExecute: checked })}
          />
        )}

        <p className="text-xs text-muted-foreground">
          Symbol hints, lot policy override and SL/TP padding aren't editable from the app yet.
        </p>
      </CardContent>
    </Card>
  );
}

function ChannelRulesTab({ channelId }: { channelId: number }) {
  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="space-y-3 p-4">
          <ManagementPolicyEditor scope={{ kind: "channel", id: channelId }} />
        </CardContent>
      </Card>
      <FollowChannelCard channelId={channelId} />
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}
